import { useState, useRef, useEffect, useLayoutEffect, useCallback, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Building2,
  LogOut,
  MessageSquare,
  Trash2,
  User,
  UserPlus,
} from "lucide-react";


import { InviteMembersDialog } from "@/features/administration/components/InviteMembersDialog";
import { useAuth } from "@/state/AuthContext";
import { useOrganizationStore } from "@/stores/organizationStore";
import { track } from "@/lib/telemetry";
import { isPlatformAdmin } from "@/utils/access";

/** Above admin drawers/backdrops (e.g. TemplateDetailPanel z-40) and app header (z-50). */
const PROFILE_MENU_Z = 200;
const MENU_GAP_PX = 8;
const MENU_MAX_HEIGHT_PX = 560;
/** SETTINGS-SHELL-1 — avatar menu width 320–360px */
const MENU_WIDTH = "22.5rem"; // 360px

type Align = "left" | "right";

export function UserProfileDropdown({ align = "left" }: { align?: Align }) {
  const { user, logout } = useAuth();
  const { getUserOrganizations, organizations } = useOrganizationStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const admin = isPlatformAdmin(user);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (user && organizations.length === 0) {
      getUserOrganizations();
    }
  }, [user, organizations.length, getUserOrganizations]);

  const updateMenuPosition = useCallback(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP_PX;
    const openUpward = spaceBelow < MENU_MAX_HEIGHT_PX && rect.top > MENU_MAX_HEIGHT_PX;

    const base: CSSProperties = {
      position: "fixed",
      zIndex: PROFILE_MENU_Z,
      width: MENU_WIDTH,
      top: undefined,
      bottom: undefined,
      left: undefined,
      right: undefined,
      maxHeight: undefined,
    };

    if (align === "right") {
      base.right = Math.max(MENU_GAP_PX, window.innerWidth - rect.right);
    } else {
      base.left = Math.max(MENU_GAP_PX, rect.left);
    }

    if (openUpward) {
      base.bottom = Math.max(MENU_GAP_PX, window.innerHeight - rect.top + MENU_GAP_PX);
      base.maxHeight = Math.min(MENU_MAX_HEIGHT_PX, rect.top - MENU_GAP_PX * 2);
    } else {
      base.top = rect.bottom + MENU_GAP_PX;
      base.maxHeight = Math.min(MENU_MAX_HEIGHT_PX, spaceBelow);
    }

    setMenuStyle(base);
  }, [open, align]);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDownCapture = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("keydown", handleKeyDownCapture, true);
    return () => document.removeEventListener("keydown", handleKeyDownCapture, true);
  }, [open]);

  // Move focus into the menu when it opens, and support arrow keys.
  useEffect(() => {
    if (!open) return;
    const items = () =>
      Array.from(
        dropdownRef.current?.querySelectorAll<HTMLElement>(
          '[role="menuitem"]:not([disabled])',
        ) ?? [],
      );
    items()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
      const list = items();
      if (list.length === 0) return;
      e.preventDefault();
      const at = list.indexOf(document.activeElement as HTMLElement);
      let next = 0;
      if (e.key === "ArrowDown") next = at < 0 ? 0 : (at + 1) % list.length;
      if (e.key === "ArrowUp") next = at <= 0 ? list.length - 1 : at - 1;
      if (e.key === "End") next = list.length - 1;
      list[next].focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const handleLogout = async () => {
    track("user.logout", { userId: user?.id });
    await logout();
  };

  const userInitial = (user?.firstName?.[0] ?? user?.email?.[0] ?? "U").toUpperCase();
  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : (user?.email ?? "User");

  function go(action: string, path: string) {
    setOpen(false);
    track("user.menu.action", { action });
    navigate(path);
  }

  const menuContent =
    open &&
    createPortal(
      <div
        ref={dropdownRef}
        style={menuStyle}
        className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl ring-1 ring-black/5 dark:border-slate-700 dark:bg-slate-900 dark:ring-white/10"
        data-testid="user-profile-menu"
        role="menu"
      >
        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="profile-menu-scroll">
          <div className="px-4 pb-2 pt-3">
            <div
              className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100"
              data-testid="profile-menu-name"
            >
              {displayName}
            </div>
          </div>


          <div className="min-h-0 shrink-0 border-t border-slate-200 py-1 dark:border-slate-700">
            <MenuItem
              icon={<User className="h-4 w-4" />}
              label="My Profile"
              onClick={() => go("profile", "/settings/profile")}
              testId="menu-profile"
            />
            {admin ? (
              <>
                <MenuItem
                  icon={<UserPlus className="h-4 w-4" />}
                  label="Invite Member"
                  onClick={() => {
                    setOpen(false);
                    track("user.menu.action", { action: "invite_member" });
                    setInviteOpen(true);
                  }}
                  testId="menu-invite"
                />
                <MenuItem
                  icon={<Building2 className="h-4 w-4" />}
                  label="Administration"
                  onClick={() => go("administration", "/administration")}
                  testId="menu-administration"
                />
              </>
            ) : null}
          </div>

          <div className="min-h-0 shrink-0 border-t border-slate-200 py-1 dark:border-slate-700">
            <MenuItem
              icon={<Bell className="h-4 w-4" />}
              label="Notification Preferences"
              onClick={() => go("notification_preferences", "/settings/notifications")}
              testId="menu-notifications"
            />
            <MenuItem
              icon={<MessageSquare className="h-4 w-4" />}
              label="Send Feedback"
              onClick={() => {
                setOpen(false);
                track("user.menu.action", { action: "feedback" });
                window.dispatchEvent(new CustomEvent("zephix:feedback-open"));
              }}
              testId="menu-feedback"
            />
          </div>

          {admin ? (
            <div className="min-h-0 shrink-0 border-t border-slate-200 py-1 dark:border-slate-700">
              <MenuItem
                icon={<Trash2 className="h-4 w-4" />}
                label="Trash"
                onClick={() => go("trash", "/administration/trash")}
                testId="menu-trash"
              />
            </div>
          ) : null}
        </div>

        <div className="shrink-0 border-t border-slate-200 dark:border-slate-700" data-testid="profile-menu-logout-bar">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void handleLogout();
            }}
            className="flex min-h-[44px] w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 focus:bg-red-50 focus:outline-none transition dark:text-red-400 dark:hover:bg-red-950/40 dark:focus:bg-red-950/40"
            data-testid="menu-logout"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </div>,
      document.body,
    );

  return (
    <div className="relative" data-testid="user-profile-dropdown">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-800 to-blue-500 text-sm font-semibold text-white shadow-sm ring-1 ring-blue-200 hover:ring-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Open account menu"
        data-testid="user-profile-button"
      >
        {userInitial}
      </button>

      {menuContent}

      {admin ? (
        <InviteMembersDialog
          isOpen={inviteOpen}
          onClose={() => setInviteOpen(false)}
        />
      ) : null}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex min-h-[44px] w-full items-center gap-3 px-4 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:text-slate-200 dark:hover:bg-slate-800 dark:focus:bg-slate-800"
      data-testid={testId}
    >
      <span className="text-slate-400 dark:text-slate-500">{icon}</span>
      <span className="flex-1">{label}</span>
    </button>
  );
}
